import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDown, ArrowUp, Layers, Share2, Sparkles, X } from 'lucide-react';
import { SelectDropdown } from '../SelectDropdown';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { PathwayButton } from './PathwayButton';
interface PathwayTrayProps {
  name: string;
  /** Prospect or company this pathway is for — appears on the PDF cover. */
  company: string;
  /** True when the company shown was picked up from the conversation notes. */
  companyFromNotes: boolean;
  /** Module ids in the order the prospect should take them. */
  items: string[];
  audience: AudienceLevel | 'any';
  cohortId: string;
  cohorts: Cohort[];
  /** Set once the rep has chosen how edits to a saved pathway should land. */
  editContext: string | null;
  onRevert: () => void;
  onNameChange: (name: string) => void;
  onCompanyChange: (company: string) => void;
  onAudienceChange: (audience: AudienceLevel | 'any') => void;
  onCohortChange: (cohortId: string) => void;
  onRemove: (moduleId: string) => void;
  onMove: (moduleId: string, direction: -1 | 1) => void;
  onClear: () => void;
  onShare: () => void;
}
export function PathwayTray({
  name,
  company,
  companyFromNotes,
  items,
  audience,
  cohortId,
  cohorts,
  editContext,
  onRevert,
  onNameChange,
  onCompanyChange,
  onAudienceChange,
  onCohortChange,
  onRemove,
  onMove,
  onClear,
  onShare
}: PathwayTrayProps) {
  // Company and pathway name both appear on the document a prospect reads, so
  // neither can be missing by the time it is generated.
  const missing: string[] = [];
  if (items.length === 0) missing.push('at least one module');
  if (!company.trim()) missing.push('a company');
  if (!name.trim()) missing.push('a pathway name');
  const canSend = missing.length === 0;
  const blockedReason = canSend ? null : `Add ${missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`} to send this pathway.`;
  return <aside aria-label="Your pathway" className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-6" data-id="element-1673" data-mp="hkh38r7zb1lu">
      <AnimatePresence initial={false} data-id="element-1674" data-mp="rhlrqxpar95t">
        {editContext && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden bg-[#30718D]/5" data-id="element-1675" data-mp="riwv4wetyewe">
            <div className="flex items-start gap-2 px-5 py-3" data-id="element-1676" data-mp="159s5kd4z9423">
              <p className="flex-1 text-xs font-medium text-[#30718D]" data-id="element-1677" data-mp="1a71t1n8t35na">
                {editContext}
              </p>
              <button type="button" onClick={onRevert} className="flex-shrink-0 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900" data-id="element-1678" data-mp="1f8r618fl9te0">
                Revert
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="border-b border-gray-100 p-5" data-id="element-1679" data-mp="2zrl56c76bxr3">
        <div className="flex items-center justify-between" data-id="element-1680" data-mp="2uqx6d4cpecmb">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1681" data-mp="2prauozcix4fm">Your pathway</h2>
          {items.length > 0 && <button type="button" onClick={onClear} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1682" data-mp="3nfcj1k8d4iuh">
              Clear
            </button>}
        </div>
        <p className="mt-1 text-sm text-gray-600" data-id="element-1683" data-mp="3dmwzwjlbxo80">
          {items.length === 0 ? 'Nothing added yet' : `${items.length} ${items.length === 1 ? 'module' : 'modules'}`}
        </p>
      </div>

      <div className="space-y-3 border-b border-gray-100 p-5" data-id="element-1684" data-mp="596kpkxlpsaz">
        <label className="block" data-id="element-1685" data-mp="57n0xkcqkz4g">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1686" data-mp="3lwkpes28pl3m">
            Company
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1687" data-mp="3qw7312lzd3cy">
              *
            </span>
          </span>
          <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="e.g. Meridian Health" required aria-required="true" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1688" data-mp="3bzoop8il239m" />
          {companyFromNotes && <span className="mt-1 flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1689" data-mp="3uv6fr9dye4ey">
              <Sparkles size={11} aria-hidden="true" data-id="element-1690" data-mp="3pddohmzpvz8" />
              From your notes — edit if that’s not right
            </span>}
        </label>

        <label className="block" data-id="element-1691" data-mp="w7sh7rux5his">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1692" data-mp="w8trhu441zzg">
            Pathway name
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1693" data-mp="2gvl699j2edro">
              *
            </span>
          </span>
          <input value={name} onChange={e => onNameChange(e.target.value)} placeholder="e.g. New Manager Track" required aria-required="true" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1694" data-mp="2qn7ohpttld3x" />
        </label>

        <div data-id="element-1695" data-mp="26zzvalw0q35s">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1696" data-mp="26zqenowv58cr">
            Built for
          </span>
          <SelectDropdown ariaLabel="Audience this pathway is built for" fullWidth scrollMenu value={audience} onChange={value => onAudienceChange(value as AudienceLevel | 'any')} options={[{
          value: 'any',
          label: 'Mixed audience'
        }, ...(Object.keys(LEVEL_LABELS) as AudienceLevel[]).map(level => ({
          value: level,
          label: LEVEL_LABELS[level]
        }))]} data-id="element-1697" data-mp="22wli02zgs0w1" />
        </div>

        <div data-id="element-1698" data-mp="2ozt6n3vd1kro">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1699" data-mp="2ozkrf5brm3db">
            Cohort (optional)
          </span>
          <SelectDropdown ariaLabel="Cohort for this pathway" fullWidth scrollMenu value={cohortId} onChange={onCohortChange} options={[{
          value: '',
          label: 'No cohort selected'
        }, ...cohorts.map(cohort => ({
          value: cohort.id,
          label: `${cohort.organization} — ${cohort.name} (${cohort.size})`
        }))]} data-id="element-1700" data-mp="1xbsprm1l7uq7" />
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto p-5" data-id="element-1701" data-mp="1626aya7r1eop">
        {items.length === 0 ? <div className="py-8 text-center" data-id="element-1702" data-mp="1157kq2r7hrvs">
            <Layers size={24} className="mx-auto text-gray-400" data-id="element-1703" data-mp="1121z4xhsd84t" />
            <p className="mx-auto mt-3 max-w-[15rem] text-sm text-gray-600" data-id="element-1704" data-mp="x185dnhnwz40">
              Add modules from the results, then put them in the order the
              prospect should take them.
            </p>
          </div> : <ul className="space-y-2" data-id="element-1705" data-mp="n67yt2v2vr3b">
            <AnimatePresence initial={false} data-id="element-1706" data-mp="ideqk50ei0mh">
              {items.map((moduleId, index) => {
            const module = moduleById(moduleId);
            if (!module) return null;
            return <motion.li key={moduleId} layout initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              x: 8
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3" data-id="element-1707" data-mp="9e9tl34hmfcf" data-mpi={index}>
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600" data-id="element-1708" data-mp="1eaajepv6iwhh">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-1709" data-mp="14joafehyx47r">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-1710" data-mp="19bz9kf4vwlz9">
                        {module.name}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500" data-id="element-1711" data-mp="vm5kpwtdq1pj">
                        {module.category}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center" data-id="element-1712" data-mp="llv5k6nsak7q">
                      <button type="button" onClick={() => onMove(moduleId, -1)} disabled={index === 0} aria-label={`Move ${module.name} earlier`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1713" data-mp="ql82xlca79ok">
                        <ArrowUp size={14} data-id="element-1714" data-mp="by9c9sl90jaw" />
                      </button>
                      <button type="button" onClick={() => onMove(moduleId, 1)} disabled={index === items.length - 1} aria-label={`Move ${module.name} later`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1715" data-mp="py0njlkdrll7">
                        <ArrowDown size={14} data-id="element-1716" data-mp="18pl0xgbxzne1" />
                      </button>
                      <button type="button" onClick={() => onRemove(moduleId)} aria-label={`Remove ${module.name}`} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#DB3725]" data-id="element-1717" data-mp="14nzkxm442szm">
                        <X size={14} data-id="element-1718" data-mp="20ysoacqq768q" />
                      </button>
                    </div>
                  </motion.li>;
          })}
            </AnimatePresence>
          </ul>}
      </div>

      <div className="border-t border-gray-100 p-5" data-id="element-1719" data-mp="2tku208ol3g42">
        <PathwayButton fullWidth onClick={onShare} disabled={!canSend} data-id="element-1720" data-mp="2oovcghrirs22">
          <Share2 size={18} data-id="element-1721" data-mp="2joh40ym0ag3i" />
          Review and Send
        </PathwayButton>
        <p className="mt-2 text-center text-xs text-gray-500" data-id="element-1722" data-mp="2jqlbtrn0zz92">
          {blockedReason ?? 'Creates a branded PDF and a shareable link.'}
        </p>
      </div>
    </aside>;
}